import { useState } from 'react'

import ToastService from 'services/toast.service'

import { CardType } from 'types/deck'
import { DOUBLE_FACED_LAYOUTS } from 'types/card'
import { CustomCard } from 'services/apiTypes/customCards.types'

import BasicCard from 'components/card/BasicCard'
import Icon from 'components/elements/Icon'
import ArchidektDropdown from 'components/elements/ArchidektDropdown'
import CustomCardService from 'services/customCards.service'
import ConfirmDeleteModal from 'components/elements/ConfirmDeleteModal'
import CustomCardOverlay from 'components/customCards/CustomCardOverlay'
import AddToOtherDeck from 'components/card/cardForm/addToOtherDeck'
import InDecks from 'components/collectionV2/cardDetails/InDecks'

import styles from './customCardImageCard.module.scss'

type Props = {
  card: CardType
  onCardEdited: (updatedCustomCard: CustomCard) => void
  onCardRemoved?: (removedCustomCardId: number) => void
  onAddToDeckState?: (customCard: CardType) => void
  noExtrasMenu?: boolean
  usedInOverlay?: boolean
}

const CustomCardImageCard = ({
  card,
  onCardEdited,
  onCardRemoved,
  noExtrasMenu = false,
  onAddToDeckState,
  usedInOverlay = false,
}: Props) => {
  const [flipped, setFlipped] = useState(false)
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [detailsOpen, setDetailsOpen] = useState(false)
  const [addToDeck, setAddToDeck] = useState(false)

  const isFlipCard = DOUBLE_FACED_LAYOUTS.includes(card.layout)

  const handleDeleteCustomCard = (): Promise<void> => {
    if (!card.customCard?.id) {
      ToastService.create('Unable to delete card that has not been created', 'Custom Card Service', 'warning')

      return new Promise(() => null)
    }

    setLoading(true)

    return CustomCardService.remove(card.customCard.id)
      .then(() => onCardRemoved && onCardRemoved(card.customCard?.id || 0))
      .catch(() => ToastService.create('Unable to delete Custom Card', 'Custom Card Service', 'error'))
      .finally(() => setLoading(false))
  }

  return (
    <>
      <div className={styles.container}>
        <BasicCard card={card} onDoubleClick={() => setDetailsOpen(true)} flipped={flipped} />

        <div className={styles.controls}>
          {!!onAddToDeckState && (
            <>
              <button className={styles.increaseButton} onClick={() => onAddToDeckState(card)}>
                <Icon name="add" />
              </button>

              <button className={styles.decreaseButton} onClick={() => onAddToDeckState(card)} disabled={!card.qty}>
                <Icon name="minus" />
              </button>
            </>
          )}

          <button onClick={() => setDetailsOpen(true)}>
            <Icon name="chevron right" />
          </button>
        </div>

        {isFlipCard && (
          <div className={styles.filpButtonContainer}>
            <button onClick={() => setFlipped(!flipped)}>
              <Icon name="refresh" />
            </button>
          </div>
        )}

        {!noExtrasMenu && (
          <div className={styles.extraMenuContainer}>
            <ArchidektDropdown
              menuClassName={styles.extraMenu}
              options={[
                {
                  label: 'Edit custom card',
                  icon: 'edit outline',
                  onClick: () => setDetailsOpen(true),
                },
                { type: 'spacer' },
                { label: 'Add to deck', icon: 'add circle', onClick: () => setAddToDeck(true) },
                {
                  label: 'Delete Custom Card',
                  icon: 'trash alternate outline',
                  onClick: () => setDeleteConfirmationOpen(true),
                  hidden: !onCardRemoved,
                },
              ]}
            />
          </div>
        )}
      </div>

      <CustomCardOverlay
        card={detailsOpen ? card : null}
        onClose={() => setDetailsOpen(false)}
        onCardEdited={onCardEdited}
      />

      <ConfirmDeleteModal
        usedInOverlay={usedInOverlay}
        delayEnabledTime={3000}
        open={deleteConfirmationOpen}
        onClose={() => setDeleteConfirmationOpen(true)}
        buttonText={`Delete Custom Card - ${card?.name}`}
        header={`Delete custom card - ${card?.name}`}
        modelClassName={styles.deleteCardModal}
        message={
          <>
            <h4>WARNING</h4>
            <p>
              Deleting a custom card will blank out this custom card from any decks it exits in. The card will become
              uneditable, but the existance of a blank custom card record will still be present to maintain the deck's
              metadata.
            </p>
            <InDecks card={card} tableOnly />
          </>
        }
        loadingMessage="Removing custom card"
        onDelete={handleDeleteCustomCard}
      />

      <AddToOtherDeck open={addToDeck} card={card} onClose={() => setAddToDeck(false)} parentControlled />
    </>
  )
}

export default CustomCardImageCard
